import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Archive,
  ChevronLeft,
  ClipboardList,
  MoreVertical,
  Paperclip,
  Send,
} from "lucide-react";
import { hitGetChatHistoryProfessionalApi } from "../../../Redux/Professional/ChatListProfessionalSlice";
import { hitGetChatDetailsProfessionalApi } from "../../../Redux/Professional/ChatDetailsProfessionalSlice";
import { timeAgo } from "../../../Helper/CommonFunctions";
import io from "socket.io-client";
import Loader from "../../Loader/Loader";
import "./Messenger.scss";
import { hitUploadMedia } from "../../../Redux/Common/UploadMediaSlice";
import { showToast } from "../../../Helper/Toast";
import profileImagePlaceholder from "../../../assets/img/profileIcon.png";
import { useGlobalState } from "../../../Providers/GlobalStateProvider";
import { ApiBaseUrl } from "../../../Utils/Constants";

const getClientName = (chat) => {
  const first = chat?.userId?.firstName?.trim() ?? "";
  const last = chat?.userId?.lastName?.trim() ?? "";
  const full = [first, last].filter(Boolean).join(" ");
  return full || "Client";
};

const getMessagePreview = (message) => {
  if (!message) return "";
  if (message.messageType === 2) return "Attachment";
  if (message.messageType === 3) return message.cardTitle || message.message || "Update";
  return message.message || "";
};

const chatHasBid = (chat) => {
  if (!chat?.bidId) return false;
  if (typeof chat.bidId === "object") return Boolean(chat.bidId._id);
  return String(chat.bidId).trim() !== "";
};

const isChatArchived = (chat, archivedIds) => {
  if (archivedIds.has(chat._id)) return true;
  return Number(chat?.isArchived) === 1 || chat?.isArchived === true;
};

const Messenger = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const chatIdFromRequest = location?.state?.chatId;

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatMenuRef = useRef(null);

  const { setGlobalLoading, setUnreadMessagesCount } = useGlobalState();

  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState("");
  const selectedChatRef = useRef("");
  selectedChatRef.current = selectedChat;

  const [messageInput, setMessageInput] = useState("");
  const [file, setFile] = useState(null);
  const [chatListData, setChatListData] = useState([]);
  const [currentOpenChat, setCurrentOpenChat] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [listTab, setListTab] = useState("active");
  const [mobileView, setMobileView] = useState("list");
  const [showChatMenu, setShowChatMenu] = useState(false);
  const [archivedChatIds, setArchivedChatIds] = useState(() => new Set());
  const [localChatSideList, setLocalChatSideList] = useState([]);

  const [professionalId, setProfessionalId] = useState(
    localStorage.getItem("professionalId"),
  );
  const [chatId, setChatId] = useState("");
  const [jobId, setJobId] = useState("");
  const [bidId, setBidId] = useState("");
  const [clientId, setClientId] = useState("");
  const [messages, setMessages] = useState([]);

  const chatHistoryData = useSelector(
    (state) => state.getChatHistoryProfessionalReducer.data,
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (loading) setGlobalLoading(true);
  }, [loading, setGlobalLoading]);

  useEffect(() => {
    if (localStorage.getItem("professionalToken") === null) {
      navigate("/professional/login");
    } else {
      dispatch(hitGetChatHistoryProfessionalApi());
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("toggleFooter", { detail: false }));
    return () => {
      window.dispatchEvent(new CustomEvent("toggleFooter", { detail: true }));
    };
  }, []);

  useEffect(() => {
    const handleOutside = (e) => {
      if (chatMenuRef.current && !chatMenuRef.current.contains(e.target)) {
        setShowChatMenu(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    if (!chatHistoryData) return;

    if (chatHistoryData.status !== true) {
      setLoading(false);
      setGlobalLoading(false);
      setCurrentOpenChat(null);
      setMessages([]);
      return;
    }

    const list = Array.isArray(chatHistoryData.data) ? chatHistoryData.data : [];
    setLoading(false);
    setGlobalLoading(false);
    setChatListData(list);

    const apiArchived = new Set(
      list.filter((c) => Number(c?.isArchived) === 1).map((c) => c._id),
    );
    setArchivedChatIds((prev) => new Set([...prev, ...apiArchived]));

    if (list.length === 0) {
      setLocalChatSideList([]);
      setCurrentOpenChat(null);
      setMessages([]);
      return;
    }

    const requestedId =
      chatIdFromRequest != null && chatIdFromRequest !== ""
        ? chatIdFromRequest
        : null;
    const currentSel = selectedChatRef.current;
    const selectedStillValid =
      currentSel && list.some((c) => c._id === currentSel);
    const preferredId =
      requestedId ?? (selectedStillValid ? currentSel : null);
    const openChat =
      (preferredId ? list.find((c) => c._id === preferredId) : null) ??
      list[0];

    setProfessionalId(openChat.professionalId?._id);
    setChatId(openChat._id);
    setJobId(openChat.jobId?._id);
    setBidId(openChat.bidId?._id ?? openChat.bidId);
    setClientId(openChat.userId?._id);
    setMessages(openChat.messages || []);
    setSelectedChat(openChat._id);

    if (requestedId || selectedStillValid) {
      setMobileView("chat");
    }

    const index = list.findIndex((chat) => chat._id === openChat._id);
    let sideList = list;
    if (index !== -1 && list[index]?.messages?.length) {
      sideList = list.map((chat, i) => {
        if (i !== index || !chat.messages?.length) return chat;
        return {
          ...chat,
          messages: [
            { ...chat.messages[0], isReadByProfessional: 1 },
            ...chat.messages.slice(1),
          ],
        };
      });
    }
    setLocalChatSideList(sideList);

    dispatch(hitGetChatDetailsProfessionalApi({ chatId: openChat._id })).then(
      (response) => {
        if (response?.payload?.status === true) {
          let unreadCount = 0;
          (response.payload.data?.messages || []).forEach((msg) => {
            if (msg.isReadByProfessional === 0) unreadCount++;
          });
          setUnreadMessagesCount((prevCount) => prevCount - unreadCount);
          setCurrentOpenChat(openChat);
          setMessages(response.payload.data?.messages || []);
        }
      },
    );
  }, [
    chatHistoryData,
    chatIdFromRequest,
    dispatch,
    setGlobalLoading,
    setUnreadMessagesCount,
  ]);

  useEffect(() => {
    const newSocket = io(ApiBaseUrl.replace(/\/$/, ""));
    setSocket(newSocket);

    newSocket.on("connect", () => {
      newSocket.emit("user_connected", {
        type: 1,
        userId: professionalId,
        socketId: newSocket.id,
      });
    });

    newSocket.on("message", (data) => {
      setMessages((prevMessages) => [...prevMessages, data]);
    });

    return () => {
      newSocket.close();
    };
  }, [professionalId]);

  useEffect(() => {
    if (file) uploadFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const filteredConversations = useMemo(() => {
    return localChatSideList.filter((chat) => {
      const archived = isChatArchived(chat, archivedChatIds);
      return listTab === "archived" ? archived : !archived;
    });
  }, [localChatSideList, listTab, archivedChatIds]);

  const messagingUnlocked = chatHasBid(currentOpenChat);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        showToast("File size should be less than 5MB", "error");
        return;
      }
      setFile(selectedFile);
    }
  };

  const uploadFile = async () => {
    if (file && !isUploading) {
      setIsUploading(true);
      setLoading(true);
      setGlobalLoading(true);
      const formData = new FormData();
      formData.append("myfile", file);
      try {
        const response = await dispatch(hitUploadMedia(formData));
        setLoading(false);
        setGlobalLoading(false);
        if (response?.payload?.status === true) {
          showToast("File uploaded successfully!", "success");
          const url = response.payload?.data?.[0]?.url;
          if (!url) {
            showToast("Upload response missing file URL", "error");
            return;
          }
          const message = {
            type: 1,
            professionalId,
            userId: clientId,
            chatId,
            jobId,
            bidId,
            url,
            message: "",
            messageType: 2,
            messageFor: 2,
            sentAt: new Date(),
          };
          if (socket) socket.emit("send_message", message);
          setMessages((prev) => [...prev, message]);
          setMessageInput("");
          setFile(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
        } else {
          showToast(response?.payload?.message || "Upload failed", "error");
        }
      } catch {
        setLoading(false);
        setGlobalLoading(false);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleSendMessage = () => {
    if (!messageInput.trim() || !socket || !messagingUnlocked) return;

    const message = {
      type: 1,
      professionalId,
      userId: clientId,
      chatId,
      jobId,
      bidId,
      message: messageInput,
    };

    socket.emit("send_message", message);

    const newMessage = {
      _id: `local-${Date.now()}`,
      message: messageInput,
      sentAt: new Date(),
      messageFor: 2,
      messageType: 1,
    };
    setMessages((prev) => [...prev, newMessage]);
    setMessageInput("");

    dispatch(hitGetChatHistoryProfessionalApi()).then(() => {
      const prepend = (prev) =>
        prev.map((chat) =>
          chat._id === chatId
            ? { ...chat, messages: [newMessage, ...(chat.messages || [])] }
            : chat,
        );
      setChatListData(prepend);
      setLocalChatSideList(prepend);
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const openConversation = useCallback(
    (chatData) => {
      setSelectedChat(chatData._id);
      setProfessionalId(chatData.professionalId?._id);
      setChatId(chatData._id);
      setJobId(chatData.jobId?._id);
      setBidId(chatData.bidId?._id ?? chatData.bidId);
      setClientId(chatData.userId?._id);
      setShowChatMenu(false);
      setMobileView("chat");

      const index = localChatSideList.findIndex(
        (chat) => chat._id === chatData._id,
      );

      if (index !== -1 && chatData.messages?.length) {
        const updatedList = localChatSideList.map((chat, i) => {
          if (i !== index || !chat.messages?.length) return chat;
          return {
            ...chat,
            messages: [
              { ...chat.messages[0], isReadByProfessional: 1 },
              ...chat.messages.slice(1),
            ],
          };
        });
        setLocalChatSideList(updatedList);
      }

      dispatch(hitGetChatDetailsProfessionalApi({ chatId: chatData._id })).then(
        (response) => {
          if (response?.payload?.status === true) {
            let unreadCount = 0;
            (chatData.messages || []).forEach((msg) => {
              if (msg.isReadByProfessional === 0) unreadCount++;
            });
            setUnreadMessagesCount((prevCount) => prevCount - unreadCount);
            setCurrentOpenChat(chatData);
            setMessages(response.payload.data?.messages || []);
          }
        },
      );
    },
    [dispatch, localChatSideList, setUnreadMessagesCount],
  );

  const handleArchiveChat = () => {
    if (!selectedChat) return;
    setArchivedChatIds((prev) => new Set([...prev, selectedChat]));
    setShowChatMenu(false);
    setMobileView("list");
    showToast("Chat archived", "success");
  };

  const handlePlaceBid = () => {
    const targetJobId = currentOpenChat?.jobId?._id ?? jobId;
    if (targetJobId) {
      navigate(`/professional/job-details/${targetJobId}`);
      return;
    }
    navigate("/professional/jobs");
  };

  const renderMessageContent = (message) => {
    if (message.messageType === 2) {
      return (
        <img src={message.url} alt="" className="pro-messenger__message-image" />
      );
    }

    if (message.messageType === 3) {
      return (
        <div className="pro-messenger__card-msg">
          <div className="pro-messenger__card-msg-head">
            <ClipboardList size={18} strokeWidth={2} aria-hidden />
            <span className="pro-messenger__card-msg-title">
              {message.cardTitle || message.title || "More Details Needed"}
            </span>
          </div>
          {message.url ? (
            <img
              src={message.url}
              alt=""
              className="pro-messenger__card-msg-thumb"
            />
          ) : null}
          {message.message ? (
            <p className="pro-messenger__card-msg-body">{message.message}</p>
          ) : null}
        </div>
      );
    }

    return <p className="pro-messenger__message-text">{message.message}</p>;
  };

  if (loading) {
    return (
      <div className="pro-messenger__loader">
        <Loader />
      </div>
    );
  }

  const mobileClass =
    mobileView === "chat"
      ? "pro-messenger__layout--mobile-chat"
      : "pro-messenger__layout--mobile-list";

  return (
    <div className="messenger-page pro-app-page pro-app-page--fill pro-messenger">
      <div className="custom_container messenger-page__container">
        <div className={`pro-messenger__layout ${mobileClass}`}>
          {/* Conversation list */}
          <aside className="pro-messenger__list-panel">
            <div className="pro-messenger__list-header">
              <h1 className="pro-messenger__list-title">Recent Conversations</h1>
            </div>

            <div className="pro-messenger__tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={listTab === "active"}
                className={`pro-messenger__tab ${
                  listTab === "active" ? "pro-messenger__tab--active" : ""
                }`}
                onClick={() => setListTab("active")}
              >
                Active Conversations
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={listTab === "archived"}
                className={`pro-messenger__tab ${
                  listTab === "archived" ? "pro-messenger__tab--active" : ""
                }`}
                onClick={() => setListTab("archived")}
              >
                Archived
              </button>
            </div>

            <div className="pro-messenger__conversations">
              {filteredConversations.length > 0 ? (
                filteredConversations.map((conv) => {
                  const lastMessage = conv.messages?.[0];
                  const unread =
                    lastMessage?.isReadByProfessional === 0;
                  return (
                    <button
                      key={conv._id}
                      type="button"
                      className={[
                        "pro-messenger__conv-item",
                        selectedChat === conv._id
                          ? "pro-messenger__conv-item--active"
                          : "",
                        unread ? "pro-messenger__conv-item--unread" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => openConversation(conv)}
                    >
                      <img
                        className="pro-messenger__conv-avatar"
                        src={
                          conv.userId?.profileImage || profileImagePlaceholder
                        }
                        alt=""
                        onError={(e) => {
                          e.currentTarget.src = profileImagePlaceholder;
                        }}
                      />
                      <div className="pro-messenger__conv-body">
                        <div className="pro-messenger__conv-top">
                          <span className="pro-messenger__conv-name">
                            {getClientName(conv)}
                          </span>
                          <span className="pro-messenger__conv-time">
                            {lastMessage ? timeAgo(lastMessage.sentAt) : ""}
                          </span>
                        </div>
                        <span className="pro-messenger__conv-job">
                          {conv.jobId?.title ?? "Job"}
                        </span>
                        <span className="pro-messenger__conv-preview">
                          {getMessagePreview(lastMessage) || "No messages yet"}
                        </span>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="pro-messenger__empty-list">
                  <p>
                    {listTab === "archived"
                      ? "No archived conversations"
                      : "No conversations yet"}
                  </p>
                  {listTab === "active" && (
                    <span>
                      When clients message you about jobs, they will appear here.
                    </span>
                  )}
                </div>
              )}
            </div>
          </aside>

          {/* Chat thread */}
          <section className="pro-messenger__chat-panel">
            <header className="pro-messenger__chat-header">
              <button
                type="button"
                className="pro-messenger__back-btn"
                aria-label="Back to conversations"
                onClick={() => setMobileView("list")}
              >
                <ChevronLeft size={22} strokeWidth={2} />
              </button>

              {currentOpenChat ? (
                <>
                  <img
                    className="pro-messenger__chat-header-avatar"
                    src={
                      currentOpenChat.userId?.profileImage ||
                      profileImagePlaceholder
                    }
                    alt=""
                    onError={(e) => {
                      e.currentTarget.src = profileImagePlaceholder;
                    }}
                  />
                  <h2 className="pro-messenger__chat-header-name">
                    {getClientName(currentOpenChat)}
                  </h2>
                  <div className="pro-messenger__chat-menu-wrap" ref={chatMenuRef}>
                    <button
                      type="button"
                      className="pro-messenger__menu-btn"
                      aria-label="Chat options"
                      onClick={() => setShowChatMenu((v) => !v)}
                    >
                      <MoreVertical size={20} strokeWidth={2} />
                    </button>
                    {showChatMenu && (
                      <div className="pro-messenger__chat-menu">
                        <button
                          type="button"
                          className="pro-messenger__chat-menu-item"
                          onClick={handleArchiveChat}
                        >
                          <Archive size={16} strokeWidth={2} aria-hidden />
                          Archive Chat
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <h2 className="pro-messenger__chat-header-name">Messenger</h2>
              )}
            </header>

            <div className="pro-messenger__messages">
              {!currentOpenChat ? (
                <div className="pro-messenger__empty-chat">
                  <p>Select a conversation to start messaging</p>
                </div>
              ) : messages.length > 0 ? (
                <div className="pro-messenger__messages-scroll">
                  {messages.map((message, idx) => {
                    const isSent = message.messageFor === 2;
                    const isCard = message.messageType === 3;
                    const showAvatar = !isSent && !isCard;
                    return (
                      <div
                        key={message._id ?? `msg-${idx}`}
                        className={[
                          "pro-messenger__message-row",
                          isSent
                            ? "pro-messenger__message-row--sent"
                            : "pro-messenger__message-row--received",
                          isCard ? "pro-messenger__message-row--card" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {showAvatar ? (
                          <img
                            className="pro-messenger__msg-avatar"
                            src={
                              currentOpenChat.userId?.profileImage ||
                              profileImagePlaceholder
                            }
                            alt=""
                            onError={(e) => {
                              e.currentTarget.src = profileImagePlaceholder;
                            }}
                          />
                        ) : null}
                        <div className="pro-messenger__message-stack">
                          <div
                            className={[
                              "pro-messenger__bubble",
                              isSent
                                ? "pro-messenger__bubble--sent"
                                : "pro-messenger__bubble--received",
                              isCard ? "pro-messenger__bubble--card" : "",
                              !isSent &&
                              message.isReadByProfessional === 0
                                ? "pro-messenger__bubble--highlight"
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            {renderMessageContent(message)}
                          </div>
                          <span className="pro-messenger__message-time">
                            {timeAgo(message.sentAt)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              ) : (
                <div className="pro-messenger__empty-chat">
                  <p>No messages yet</p>
                </div>
              )}
            </div>

            {currentOpenChat && (
              <footer className="pro-messenger__composer">
                {messagingUnlocked ? (
                  <div className="pro-messenger__input-row">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleFileChange}
                      className="pro-messenger__file-input"
                      id="pro-messenger-file"
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="pro-messenger-file"
                      className={`pro-messenger__attach-btn ${
                        isUploading ? "pro-messenger__attach-btn--disabled" : ""
                      }`}
                      title="Attach file"
                    >
                      <Paperclip size={18} strokeWidth={2} />
                    </label>
                    <textarea
                      placeholder="Type your message..."
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyDown={handleKeyPress}
                      className="pro-messenger__textarea"
                      rows={1}
                    />
                    <button
                      type="button"
                      className="pro-messenger__send-btn"
                      onClick={handleSendMessage}
                      disabled={!messageInput.trim()}
                      aria-label="Send message"
                    >
                      <Send size={18} strokeWidth={2} />
                    </button>
                  </div>
                ) : (
                  <div className="pro-messenger__bid-cta">
                    <p className="pro-messenger__bid-cta-text">
                      Want to keep chatting? Place a bid to unlock full messaging.
                    </p>
                    <button
                      type="button"
                      className="pro-messenger__bid-cta-btn"
                      onClick={handlePlaceBid}
                    >
                      Place a bid to start
                    </button>
                  </div>
                )}
              </footer>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Messenger;
